import moment from 'moment';
import dateFormats from '../../app/data/common/dateFormats';
import { LinehaulTrip } from '../../app/data/linehaul-mileage/models';

const EXCEPTION_TYPES = ['DELAY', 'OVERNIGHT', 'REIMBURSEMENT', 'BREAKDOWN'] as const;

const escapeCSVValue = (value: string | number | null | undefined): string => {
  const str = value == null ? '' : String(value);
  // Wrap in quotes if contains comma, newline, or double quote
  if (str.includes(',') || str.includes('\n') || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const exportTripsToCSV = (data: LinehaulTrip[]) => {
  const exceptionColumns = EXCEPTION_TYPES.flatMap(type => [
    `${type} - Duration`,
    `${type} - Amount`,
    `${type} - Reason`,
    `${type} - Notes`,
  ]);

  const csvHeader = [
    'Manifest #',
    'Dispatch Date',

    // Driver
    'Driver ID',
    'Driver First Name',
    'Driver Last Name',

    // Lane
    'Lane Name',
    'Lane Miles',
    'Lane Notes',
    'Relay City',
    'Relay State',
    'Relay Zipcode',

    // Origin Terminal
    'Origin Terminal Airport Code',
    'Origin Terminal Name',
    'Origin Terminal Number',

    // Destination Terminal
    'Destination Terminal Airport Code',
    'Destination Terminal Name',
    'Destination Terminal Number',

    // Status
    'Status',

    // Exceptions (4 types × 4 fields each)
    ...exceptionColumns,

    // Audit
    'Last Updated At',
    'Last Updated By',
  ];

  const csvRows = data.map(row => {
    // --- Exception lookup map ---
    const exceptionMap = Object.fromEntries(
      EXCEPTION_TYPES.map(type => [type, row.exceptions?.find(e => e.type === type) ?? null])
    );

    const exceptionValues = EXCEPTION_TYPES.flatMap(type => {
      const exc = exceptionMap[type];
      return [
        exc?.duration ?? '',
        exc?.amount ?? '',
        exc?.reason ?? '',
        exc?.notes ?? '',
      ];
    });

    const values = [
      row.manifestNumber,
      moment(row.dispatchedDate, 'YYYYMMDD').format(dateFormats.uiDate),

      // Driver
      row.driver?.driverId ?? '',
      row.driver?.firstName ?? '',
      row.driver?.lastName ?? '',

      // Lane
      row.lane?.laneName ?? '',
      row.lane?.miles ?? '',
      row.lane?.laneNotes ?? '',
      row.lane?.relayCity ?? '',
      row.lane?.relayState ?? '',
      row.lane?.relayZipcode ?? '',

      // Origin Terminal
      row.originTerminalAirportcode ?? '',
      row.originTerminalName ?? '',
      row.originTerminalNumber ?? '',

      // Destination Terminal
      row.destinationTerminalAirportcode ?? '',
      row.destinationTerminalName ?? '',
      row.destinationTerminalNumber ?? '',

      // Status
      row.isReviewed ? 'REVIEWED' : 'NOT REVIEWED',

      // Exceptions
      ...exceptionValues,

      // Audit
      row.updatedAt ? moment(row.updatedAt).format(dateFormats.uiDate) : '',
      row.updatedBy ?? '',
    ];

    return values.map(escapeCSVValue).join(',');
  });

  const csvContent = [csvHeader.map(escapeCSVValue).join(','), ...csvRows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `trips-${moment().format(dateFormats.uiDate)}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default exportTripsToCSV;